import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  usePopper,
  FilterButton,
  SearchableTextField,
  Button,
  PlusIcon,
  useTranslations,
  Tooltip,
  ActionButton,
  Divider,
  ShareIcon,
  DeleteIcon,
  usePubSub,
  encryptSymmetric,
  useSnackbar,
  ErrorIcon,
  useMapKeyValueExtractor,
  PromptModal,
  PanelContent,
  ISearchableTextFieldProps,
  IFilterButtonProps,
} from '@uniqkey-frontend/shared-app';
import { Ownership } from '@uniqkey-backend-organization-web/api-client';
import PubSubEventEnum from '../../../../enums/PubSubEventEnum';
import OrganizationLoginsTable, {
  IOrganizationLoginsTableRow,
} from '../../../../components/tables/OrganizationLoginsTable';
import useOrganizationLoginsTable from '../../../../hooks/tables/useOrganizationLoginsTable';
import OrganizationLoginsTabFilter, {
  IOrganizationLoginsTabFilterSubmitResult,
} from './components/OrganizationLoginsTabFilter';
import CreateLoginModal, { ICreateLoginReturnValue } from '../../../../components/CreateLoginModal';
import useVaultsAPI from '../../../../hooks/useVaultsAPI';
import { useCompanionApplicationContext } from '../../../../contexts/CompanionApplicationContext';
import { TSelectionOptionType } from '../../../../components/SelectionModalOption';
import ShareVaultsToEmployeeModal from '../../../../components/ShareVaultsToEmployeeModal';
import ShareVaultsToGroupModal from '../../../../components/ShareVaultsToGroupModal';
import ShareVaultsToEmployeeOrGroupSelectionModal
  from '../../../../components/ShareVaultsToEmployeeOrGroupSelectionModal';
import { useDetachVaults, useDeleteVaults } from '../../../../hooks/reactQuery';
import VaultTypeEnum from '../../../../enums/VaultTypeEnum';
import { logException } from '../../../../services/sentryService';
import HostTypeEnum from '../../../../enums/HostTypeEnum';
import { getActiveOrganizationId } from '../../../../services/organizationService';
import { useTrustedPortalStore } from '../../../../modules/TrustedPortalModule/store';

const OrganizationLoginsTab = () => {
  const { t } = useTranslations();
  const [isCreateLoginModalOpen, setIsCreateLoginModalOpen] = useState<boolean>(false);
  const [isShareLoginsModalOpen, setIsShareLoginsModalOpen] = useState<boolean>(false);
  const [shareLoginsModalType, setShareLoginsModalType] = useState<
    TSelectionOptionType | undefined
  >(undefined);
  const [isCreateLoginLoading, setIsCreateLoginLoading] = useState<boolean>(false);
  const [isDetachLoginModalOpen, setIsDetachLoginModalOpen] = useState<boolean>(false);
  const [isDetachLoginLoading, setIsDetachLoginLoading] = useState<boolean>(false);
  const [isDeleteLoginModalOpen, setIsDeleteLoginModalOpen] = useState<boolean>(false);
  const [isDeleteLoginLoading, setIsDeleteLoginLoading] = useState<boolean>(false);
  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );
  const {
    selectedLogins,
    refetch,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps
  } = useOrganizationLoginsTable({
    noDataMessageKey: 'organizationLoginsPage.noData',
  });
  const {
    values: selectedLoginsAsObjects, keys: selectedLoginsIds,
  } = useMapKeyValueExtractor<IOrganizationLoginsTableRow>(selectedLogins);

  const handleFilterClose = useCallback(
    () => setIsFilterOpen(false),
    [setIsFilterOpen],
  );
  const handleCreateLoginModalOpen = useCallback(() => setIsCreateLoginModalOpen(true), []);
  const handleCreateLoginModalClose = useCallback(() => setIsCreateLoginModalOpen(false), []);
  const handleDetachLoginModalOpen = useCallback(() => setIsDetachLoginModalOpen(true), []);
  const handleDetachLoginModalClose = useCallback(() => setIsDetachLoginModalOpen(false), []);
  const handleDeleteLoginModalOpen = useCallback(() => setIsDeleteLoginModalOpen(true), []);
  const handleDeleteLoginModalClose = useCallback(() => setIsDeleteLoginModalOpen(false), []);
  const handleShareLoginsModalClose = useCallback(() => setIsShareLoginsModalOpen(false), []);
  const handleShareLoginsModalTypeChange = useCallback((type: TSelectionOptionType) => {
    setShareLoginsModalType(type);
    handleShareLoginsModalClose();
  }, [handleShareLoginsModalClose]);
  const handleShareLoginsModalTypeClose = useCallback(() => setShareLoginsModalType(undefined), []);
  const handleShareLoginsModalOpen = useCallback(
    () => (selectedLoginsAsObjects[0].ownership === Ownership.Groups
      ? handleShareLoginsModalTypeChange(Ownership.Groups)
      : setIsShareLoginsModalOpen(true)),
    [selectedLoginsAsObjects, handleShareLoginsModalTypeChange],
  );
  const { createVaultPasswords } = useVaultsAPI();
  const { showError, showWarning, showSuccess } = useSnackbar();
  const { symmetricKey } = useCompanionApplicationContext();
  const { mutate: mutateDetachVault } = useDetachVaults();
  const { mutate: mutateDeleteVault } = useDeleteVaults();

  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;

  const handleRefetch = useCallback(() => refetch(true), [refetch]);
  usePubSub(PubSubEventEnum.DATA_SYNCHRONIZATION_VAULT, handleRefetch);

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((
    updatedValues: IOrganizationLoginsTabFilterSubmitResult,
  ) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  const {
    isShareDisabled,
    isRemoveDisabled,
    isDeleteDisabled,
    shareTooltipTitle,
    removeTooltipTitle,
    deleteTooltipTitle,
  } = useMemo(() => {
    if (!selectedLoginsAsObjects.length) {
      return {
        isShareDisabled: true,
        isRemoveDisabled: true,
        isDeleteDisabled: true,
        shareTooltipTitle: t('common.tickRow'),
        removeTooltipTitle: t('common.tickRow'),
        deleteTooltipTitle: t('common.tickRow'),
      };
    }
    let disableShare = false;
    let disableRemove = false;
    let disableDelete = false;
    let shareTitle = t('organizationLoginsPage.share');
    let removeTitle = t('organizationLoginsPage.remove');
    let deleteTitle = t('organizationLoginsPage.delete');
    selectedLoginsAsObjects.forEach(({ ownership }) => {
      if (ownership !== selectedLoginsAsObjects[0].ownership) {
        disableShare = true;
        disableRemove = true;
        disableDelete = true;
        shareTitle = t('common.valuesInManagedByShouldBeIdentical');
        removeTitle = t('common.valuesInManagedByShouldBeIdentical');
        deleteTitle = t('common.valuesInManagedByShouldBeIdentical');
      }
      if (ownership === Ownership.Unmanaged) {
        disableRemove = true;
      }
      if (ownership === Ownership.Employees) {
        disableShare = true;
        disableDelete = true;
      }
      if (ownership === Ownership.Groups) disableDelete = true;
    });
    return {
      isShareDisabled: disableShare,
      isRemoveDisabled: disableRemove,
      isDeleteDisabled: disableDelete,
      shareTooltipTitle: shareTitle,
      removeTooltipTitle: removeTitle,
      deleteTooltipTitle: deleteTitle,
    };
  }, [selectedLoginsAsObjects, t]);

  const ownership = useMemo(() => {
    if (selectedLoginsAsObjects.length) {
      return selectedLoginsAsObjects[0].ownership;
    }
    return null;
  }, [selectedLoginsAsObjects]);

  const handleCreateLogin = useCallback(async (loginParams: ICreateLoginReturnValue) => {
    const {
      name, additionalInfo, username, password, hostType, website, desktopApp,
    } = loginParams;
    setIsCreateLoginLoading(true);
    try {
      let payload = {
        name,
        username,
        password: encryptSymmetric({
          key: symmetricKey,
          string: password,
          fallbackValue: null,
        }),
        applicationNames: ['todo-remove-me-later.com'], // TODO: remove this
        additionalInfo,
      };
      if (hostType === HostTypeEnum.Website) {
        payload = { ...payload, ...(website && { webAddress: website }) };
      }
      if (hostType === HostTypeEnum.DesktopApp) {
        payload = {
          ...payload,
          ...(desktopApp?.value && {
            desktopApplicationName: desktopApp.value,
            applicationId: desktopApp.id,
          }),
        };
      }
      await createVaultPasswords(payload);
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.login.created'),
        });
      } else {
        showWarning({
          text: t('createLoginModal.loginWillBeShown'),
        });
      }
      handleCreateLoginModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'OrganizationLoginsPage/handleCreateLogin exception',
      });
    } finally {
      setIsCreateLoginLoading(false);
    }
  }, [
    symmetricKey,
    handleCreateLoginModalClose,
    createVaultPasswords,
    isTrustedPortalEnabled,
    showSuccess,
    showWarning,
    showError,
    t,
  ]);

  const handleDetachLogin = useCallback(async () => {
    setIsDetachLoginLoading(true);
    mutateDetachVault({
      vaultIds: selectedLoginsIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationLoginsPage.detachLoginModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t('organizationLoginsPage.detachLoginModal.errorMessage', { count: failCount }),
          });
        }
        handleDetachLoginModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: () => showError({ text: t('common.somethingWentWrong') }),
      onSettled: () => setIsDetachLoginLoading(false),
    });
  }, [
    selectedLoginsIds,
    mutateDetachVault,
    handleDetachLoginModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  const handleDeleteLogin = useCallback(async () => {
    setIsDeleteLoginLoading(true);
    mutateDeleteVault({
      vaultIds: selectedLoginsIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationLoginsPage.deleteLoginModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t('organizationLoginsPage.deleteLoginModal.errorMessage', { count: failCount }),
          });
        }
        handleDeleteLoginModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: () => showError({ text: t('common.somethingWentWrong') }),
      onSettled: () => setIsDeleteLoginLoading(false),
    });
  }, [
    selectedLoginsIds,
    mutateDeleteVault,
    handleDeleteLoginModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  return (
    <PanelContent p={0}>
      <Grid container justifyContent="space-between" alignItems="stretch" p={1}>
        <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
          <Grid item>
            <Tooltip title={t('common.filter')}>
              <FilterButton
                isFilterActive={isFilterActive}
                numberOfActiveFilters={numberOfActiveFilters}
                selected={isFilterOpen}
                onChange={toggleIsFilterOpen}
              />
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={shareTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleShareLoginsModalOpen}
                disabled={isShareDisabled}
              >
                <ShareIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={removeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDetachLoginModalOpen}
                disabled={isRemoveDisabled}
              >
                <ErrorIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={deleteTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDeleteLoginModalOpen}
                disabled={isDeleteDisabled}
              >
                <DeleteIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
        </Grid>
        <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
          <Grid item>
            <SearchableTextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('common.search')}
            />
          </Grid>
          <Box mr={3} />
          <Grid item>
            <Button
              icon={<PlusIcon />}
              onClick={handleCreateLoginModalOpen}
            >
              {t('organizationLoginsPage.createLoginButton')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <OrganizationLoginsTable
        selectedLogins={selectedLogins}
        {...restTableProps}
      />
      {isCreateLoginModalOpen && (
        <CreateLoginModal
          isOpen={isCreateLoginModalOpen}
          onClose={handleCreateLoginModalClose}
          onSubmit={handleCreateLogin}
          isLoading={isCreateLoginLoading}
        />
      )}
      {isDetachLoginModalOpen && (
        <PromptModal
          open={isDetachLoginModalOpen}
          onClose={handleDetachLoginModalClose}
          onSubmit={handleDetachLogin}
          title={t(
            `organizationLoginsPage.detachLoginModal.detachFrom${ownership}.title`,
            { count: selectedLogins.size },
          )}
          description={t(
            `organizationLoginsPage.detachLoginModal.detachFrom${ownership}.description`,
          )}
          additionalDescription={t(
            `organizationLoginsPage.detachLoginModal.detachFrom${ownership}.additionalDescription`,
          )}
          list={selectedLoginsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDetachLoginLoading}
        />
      )}
      {isDeleteLoginModalOpen && (
        <PromptModal
          open={isDeleteLoginModalOpen}
          onClose={handleDeleteLoginModalClose}
          onSubmit={handleDeleteLogin}
          title={t(
            'organizationLoginsPage.deleteLoginModal.title',
            { count: selectedLogins.size },
          )}
          description={t(
            'organizationLoginsPage.deleteLoginModal.description',
          )}
          approvalButtonText="common.delete"
          list={selectedLoginsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDeleteLoginLoading}
        />
      )}
      {isShareLoginsModalOpen && (
        <ShareVaultsToEmployeeOrGroupSelectionModal
          isOpen={isShareLoginsModalOpen}
          onClose={handleShareLoginsModalClose}
          onSelect={handleShareLoginsModalTypeChange}
          type={VaultTypeEnum.Password}
        />
      )}
      {shareLoginsModalType === Ownership.Employees && (
        <ShareVaultsToEmployeeModal
          isOpen={shareLoginsModalType === Ownership.Employees}
          onClose={handleShareLoginsModalTypeClose}
          vaults={selectedLoginsAsObjects}
          type={VaultTypeEnum.Password}
          onSubmit={resetSelectedRows}
        />
      )}
      {shareLoginsModalType === Ownership.Groups && (
        <ShareVaultsToGroupModal
          isOpen={shareLoginsModalType === Ownership.Groups}
          onClose={handleShareLoginsModalTypeClose}
          vaults={selectedLoginsAsObjects}
          type={VaultTypeEnum.Password}
          onSubmit={resetSelectedRows}
        />
      )}
      <OrganizationLoginsTabFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
    </PanelContent>
  );
};

export default OrganizationLoginsTab;
